// ./src/components/features/Venues/VenuesFilterBar/VenuesFilterBar.tsx
//
// Filter controls for the Venues list: by status (active / disabled) and by one
// or more categories. Fully controlled — the page owns the value and maps it to a
// VenueFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import { VenueCategorySelect } from "../VenueCategorySelect/VenueCategorySelect";

export type VenueStatusFilter = "active" | "disabled";

export interface VenuesFilterValue {
  status: VenueStatusFilter | null;
  categoryIds: string[];
}

export interface VenuesFilterBarProps {
  value: VenuesFilterValue;
  onChange: (value: VenuesFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  /** Restrict the category filter to these groups (e.g. ["service"] for vendors). */
  categoryGroups?: string[];
}

export const EMPTY_VENUES_FILTER: VenuesFilterValue = {
  status: null,
  categoryIds: [],
};

const isFilterActive = (value: VenuesFilterValue): boolean =>
  Boolean(value.status) || value.categoryIds.length > 0;

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };
const CATEGORY_STYLE: React.CSSProperties = { flex: "1 1 280px", minWidth: 240 };

const ANY = "any";

const VenuesFilterBarComponent: React.FC<VenuesFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
  categoryGroups,
}) => {
  const { t } = useTranslation(["venues", "common"]);

  const statusOptions = [
    { value: ANY, label: t("venues:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("venues:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("venues:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("venues:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as VenueStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={CATEGORY_STYLE}>
        <VenueCategorySelect
          label={t("venues:filters.categories.label", { defaultValue: "Categories" })}
          placeholder={t("venues:filters.categories.placeholder", { defaultValue: "All categories" })}
          value={value.categoryIds}
          onChange={(categoryIds) => onChange({ ...value, categoryIds })}
          disabled={disabled}
          allowedGroups={categoryGroups}
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_VENUES_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const VenuesFilterBar = React.memo(VenuesFilterBarComponent);
